/* CaseRecordsManager
   ───────────────────
   Slot-based records manager for an aligner case. Used on the doctor's
   Case Detail page (Submission tab) and as the post-submission counterpart
   to the wizard's Records step.

   Doctors can:
     - Upload a file into an empty slot (when case is editable)
     - Replace the file in a slot — backend auto-soft-deletes the old one
     - Remove a file outright

   Edit affordances are status-gated. Once a case is past info_requested
   the slot tiles become read-only previews — the planner is downstream
   from these records and surprise overwrites would break their work.

   Backend contract (unchanged):
     - POST  /api/cases/{id}/upload?kind=<slot>   FormData file
              → auto-replaces the existing file in that slot
     - DELETE /api/cases/files/{file_id}          → soft-deletes file
     - GET   /api/cases/metadata                  → slot catalogue
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
    Upload, X as CloseIcon, FileArrowUp, Spinner, LockKey, ArrowsClockwise,
} from "@phosphor-icons/react";
import { confirmDialog } from "@/lib/confirm";

/* Statuses where the doctor can still freely edit the records that went
   to the planner. Anything beyond `revision_requested` (i.e. plan in
   review, approved, in production, shipped, etc.) is locked — overwriting
   records after that point would invalidate downstream planner work.

   iter98.99ae (2026-06) — Tightened to drop `submitted` and
   `assigned_to_planner` per user direction: "once the case is
   submitted from the doctor's end, please make the submission tab
   read only". The doctor can still edit records when the planner
   bounces the case back for `info_requested` or `revision_requested`. */
const EDITABLE_STATUSES = new Set([
    "draft", "info_requested", "revision_requested",
]);

const GROUP_LABEL = {
    intraoral: "Intraoral photos",
    extraoral: "Extraoral photos",
    xrays: "X-rays",
    scans: "3D scans (STL / PLY / OBJ)",
};
const GROUP_ORDER = ["intraoral", "extraoral", "xrays", "scans"];

export default function CaseRecordsManager({ caseId, caseStatus, files, onChange, inline = false }) {
    const [slots, setSlots] = useState(null);
    const editable = EDITABLE_STATUSES.has(caseStatus);

    useEffect(() => {
        let cancelled = false;
        api.get("/cases/metadata")
            .then((r) => { if (!cancelled) setSlots(r.data?.file_slots || []); })
            .catch(() => { if (!cancelled) setSlots([]); });
        return () => { cancelled = true; };
    }, []);

    /* Group slots so we render: Intraoral → Extraoral → X-rays → Scans */
    const grouped = useMemo(() => {
        if (!slots) return null;
        const g = {};
        slots.forEach((s) => { (g[s.group] = g[s.group] || []).push(s); });
        return g;
    }, [slots]);

    /* Map kind -> latest active file (defensive: backend should only return
       one undeleted file per slot, but guard anyway). */
    const fileByKind = useMemo(() => {
        const m = {};
        (files || []).forEach((f) => { if (!f.is_deleted) m[f.kind] = f; });
        return m;
    }, [files]);

    if (!grouped) {
        return (
            <div className="text-xs text-muted-foreground p-4 rounded-xl bg-secondary/30">
                Loading records…
            </div>
        );
    }

    return (
        <div
            className={inline
                ? "border-t border-border"
                : "rounded-2xl border border-border bg-card overflow-hidden"}
            data-testid="case-records-manager"
        >
            {/* iter98.99af — Heading removed in inline mode per user direction;
                records sit cleanly inside the parent Brief card. Non-inline
                usage (if/when re-introduced) still gets the chrome. */}
            {!inline && (
                <div className="px-5 py-4 border-b border-border bg-secondary/20 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                            Records you sent with this case
                        </div>
                        {editable && (
                            <div className="text-[11px] text-muted-foreground mt-1 leading-snug max-w-2xl">
                                Uploaded a wrong scan or photo? Replace or remove any record below — the planning team will only see the latest version.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="px-5 py-4 space-y-5">
                {GROUP_ORDER.map((groupKey) => {
                    const groupSlots = grouped[groupKey];
                    if (!groupSlots || groupSlots.length === 0) return null;
                    const groupHasAny = groupSlots.some((s) => fileByKind[s.key]);
                    // If the case is locked AND no file was uploaded in this group,
                    // skip rendering the empty group to keep the locked view tidy.
                    if (!editable && !groupHasAny) return null;
                    return (
                        <div key={groupKey}>
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
                                {GROUP_LABEL[groupKey] || groupKey}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {groupSlots.map((slot) => (
                                    <SlotTile
                                        key={slot.key}
                                        slot={slot}
                                        file={fileByKind[slot.key]}
                                        caseId={caseId}
                                        editable={editable}
                                        onChanged={onChange}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── A single slot row ─── */
function SlotTile({ slot, file, caseId, editable, onChanged }) {
    const [busy, setBusy] = useState(null); // 'upload' | 'remove' | null
    const inputRef = useRef(null);

    const pickFile = () => inputRef.current?.click();

    const upload = async (f) => {
        if (!f) return;
        setBusy("upload");
        const form = new FormData();
        form.append("file", f);
        try {
            await api.post(`/cases/${caseId}/upload?kind=${slot.key}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(file ? `${slot.label} replaced` : `${slot.label} uploaded`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(null);
        }
    };

    const remove = async () => {
        if (!file) return;
        if (!(await confirmDialog({ title: `Remove "${file.name}" from this case?`, confirmText: "Remove", tone: "danger" }))) return;
        setBusy("remove");
        try {
            await api.delete(`/cases/files/${file.id}`);
            toast.success(`${slot.label} removed`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not remove file");
        } finally {
            setBusy(null);
        }
    };

    const isImage = file && (file.content_type || "").startsWith("image/");
    const fileHref = file
        ? `${API}/files/${file.id}?auth=${localStorage.getItem("ca_token")}`
        : null;

    /* ── Uploaded state ── */
    if (file) {
        return (
            <div
                className="flex items-center gap-3 p-2.5 rounded-xl border border-emerald-200 bg-emerald-50/40"
                data-testid={`case-records-slot-${slot.key}`}
            >
                {/* Preview */}
                {isImage ? (
                    <a
                        href={fileHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-border shrink-0"
                        title="Open file"
                    >
                        <img
                            src={fileHref}
                            alt={file.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </a>
                ) : (
                    <a
                        href={fileHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-lg bg-white border border-border grid place-items-center shrink-0 text-muted-foreground hover:text-primary"
                        title="Open file"
                    >
                        <FileArrowUp size={16} weight="bold" />
                    </a>
                )}

                {/* Slot label + filename */}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{slot.label}</div>
                    <a
                        href={fileHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-primary hover:underline truncate block max-w-full"
                        title={file.name}
                    >
                        {file.name}
                    </a>
                </div>

                {/* Hidden input for replace */}
                <input
                    ref={inputRef}
                    type="file"
                    accept={slot.accepts}
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(f);
                        e.target.value = "";
                    }}
                />

                {/* Actions */}
                {editable && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={pickFile}
                            disabled={!!busy}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
                            data-testid={`case-records-slot-${slot.key}-replace`}
                            title="Replace this file"
                        >
                            {busy === "upload"
                                ? <Spinner size={10} className="animate-spin" />
                                : <ArrowsClockwise size={10} weight="bold" />}
                            Replace
                        </button>
                        <button
                            type="button"
                            onClick={remove}
                            disabled={!!busy}
                            className="w-7 h-7 rounded-full grid place-items-center text-muted-foreground hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                            data-testid={`case-records-slot-${slot.key}-remove`}
                            title="Remove this file"
                        >
                            {busy === "remove"
                                ? <Spinner size={11} className="animate-spin" />
                                : <CloseIcon size={11} weight="bold" />}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    /* ── Empty state ── Hidden entirely when the case is locked so a "Click
       to upload" affordance can never appear on a non-editable record. The
       parent already strips groups with zero files when locked; this guard
       handles the mixed-group case (e.g. only intraoral_front uploaded,
       the rest of the group should disappear too). */
    if (!editable) return null;

    return (
        <label
            className="flex items-center gap-3 p-2.5 rounded-xl border border-dashed border-border cursor-pointer hover:bg-secondary/30"
            data-testid={`case-records-slot-${slot.key}`}
        >
            <input
                type="file"
                accept={slot.accepts}
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload(f);
                    e.target.value = "";
                }}
            />
            <div className="w-12 h-12 rounded-lg bg-secondary text-muted-foreground grid place-items-center shrink-0">
                {busy === "upload"
                    ? <Spinner size={16} className="animate-spin" />
                    : <Upload size={16} />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{slot.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                    {busy === "upload" ? "Uploading…" : `Click to upload (${slot.accepts})`}
                </div>
            </div>
        </label>
    );
}
